import { ReactElement, JSXElementConstructor, ReactFragment } from 'react';
import area from '@turf/area';
import { StyledVCLabel, StyledVcLabelNormal } from '../styles/vaStyles';

export const isInRange = (n: any, nStart: any, nEnd: any) => {
  return n >= nStart && n <= nEnd;
};

export const getRange = (value: any) => {
  const range = [{ HIGH: [0.65, 1000] }, { LOW: [0, 0.59] }, { MEDIUM: [0.6, 0.64] }];

  const values = Number(value).toFixed(2);
  return range.map((item) => {
    const itemValues = Object.values(item)[0];
    const itemKey = Object.keys(item)[0];

    const start = itemValues[0];
    const end = itemValues[1];
    const val = isInRange(values, start, end);
    return val ? itemKey : '';
  });
};

export const getValueLabel = (value: string | number) => {
  const val = value !== '' ? getRange(value).find((item) => item !== '') : 'N/A';
  return val;
};

export const checkIfVIForFieldIsHigh = (t: (arg0: string | undefined) => any, fieldVI: any) => {
  return t(getValueLabel(Number(fieldVI))) === t('HIGH');
};

export const getVCLabel = (VI: any, t: (arg0: string | undefined) => string | number | boolean | ReactElement<any, string | JSXElementConstructor<any>> | ReactFragment | null | undefined) => {
  if (checkIfVIForFieldIsHigh(t, VI)) {
    return <StyledVCLabel>{t(getValueLabel(VI))}</StyledVCLabel>;
  } else {
    return <StyledVcLabelNormal className='indexValue'>{t(getValueLabel(VI))}</StyledVcLabelNormal>;
  }
};

export const getIndexValue = (index:any) => {
  return Number(index).toFixed(2);
};

export const sortFieldData = (fieldData:any, isFilter = false) => {
  const data:any = [];
  fieldData
    .filter((item:any) => item.productivity_zones.length > 0)
    .map((item:any) => {
      if (Object.keys(item.variability_index).length > 0) {
        data.push({
          id: item.id,
          VI: Number(item.variability_index.vxc).toFixed(2)
        });
      } else {
        data.push({
          id: item.id,
          VI: null
        });
      }
    });
  const sortedData = data.sort((a:any, b:any) => {
    if (a.VI === null) {
      return 1;
    }
    if (b.VI === null) {
      return -1;
    }
    return b.VI - a.VI;
  });
  const filteredData = isFilter
    ? fieldData.filter((item:any) => item.productivity_zones.length > 0)
    : fieldData;
  return sortedFieldData(sortedData, filteredData);
};

const sortedFieldData = (sortedArray:any, newArray:any) => {
  // Create a map of id to index from the sortedArray
  const orderMap = new Map(sortedArray.map((item:any, index:any) => [item.id, index]));

  // Sort the newArray based on the order in sortedArray
  return newArray.sort((a:any, b:any) => {
    const indexA:any = orderMap.get(a.id);
    const indexB:any = orderMap.get(b.id);

    // If both ids are in the sortedArray, sort based on their order
    if (indexA !== undefined && indexB !== undefined) {
      return indexA - indexB;
    }

    // If only one id is in the sortedArray, prioritize it
    if (indexA !== undefined) {
      return -1;
    }
    if (indexB !== undefined) {
      return 1;
    }

    // If neither id is in the sortedArray, maintain their original order
    return 0;
  });
};

export const getTooltipLablesForVC = (t:any) => {
  return {
    Variability: (
      <>
        <div>
          {t(
            `The Heterogeneity Index measures how heterogeneous the field has been in terms of crop performance.`
          )}
        </div>
        <div>
          {t(
            `The heterogeneity index utilizes data from the last 5 years and scales from 0 and 1.`
          )}
        </div>
        <div>
          {t(
            `A higher heterogeneity means that crop performance across the field has been highly variable.`
          )}
          <br />
          {t(
            `Low values indicate that crop performance across the field has been rather consistent.`
          )}
        </div>
      </>
    ),
    Consistency: (
      <>
        <div>
          {t(
            `Consistency index answers the question if the field zones of various productivity remain consistent over time.`
          )}
        </div>
        <div>
          {t(`The consistency index utilizes data from the last 5 years and scales from 0 and 1`)}
        </div>
        <div>
          {t(
            `Low values indicate that the areas of the field that had higher or lower crop performance have changed over time whereas higher values mean that those areas have stayed in similar locations over time and are more consistent`
          )}
        </div>
      </>
    ),
    VCIndex: (
      <>
        <div>
          {t(
            `Variability index measures how variable the crop performance is in the field. A higher number means the field is highly variable and is more likely to benefit from Precision Farming approaches.`
          )}
          <br />
        </div>
        <div>
          {t(
            `The variability index is calculated based on a measure of how heterogeneous the crop performance is within the field, and how consistent that heterogeneity has been over time.The index uses NDVI data from the last 5 years and ranges from 0 to 1.`
          )}
        </div>
        <div>
          {t(
            `A field with a high Variability Index means that The field has contrasting zones with low and high productivity Those zones were in the same part of the field across seasons
For a field with a Variability Index higher than 0.65 we recommend using variable seeding rates via Cropwise Planting`
          )}
        </div>
      </>
    )
  };
};

export const zoneColors = [
  {
    number_of_zones: 1,
    colors: ['#9DC72D']
  },
  {
    number_of_zones: 2,
    colors: ['#FF3E1F', '#9DC72D']
  },
  {
    number_of_zones: 3,
    colors: ['#FF3E1F', '#F8C71D', '#9DC72D']
  },
  {
    number_of_zones: 4,
    colors: ['#FF3E1F', '#F8C71D', 'CDE334', '#9DC72D']
  },
  {
    number_of_zones: 5,
    colors: ['#FF3E1F', '#FF9B63', '#F8C71D', 'CDE334', '#9DC72D']
  },
  {
    number_of_zones: 6,
    colors: ['#FF3E1F', '#FF9B63', '#E7BC72', '#F8C71D', 'CDE334', '#9DC72D']
  },
  {
    number_of_zones: 7,
    colors: ['#FF3E1F', '#FF9B63', '#E7BC72', '#F8C71D', 'CDE334', '#9DC72D', '#4D9265']
  },
  {
    number_of_zones: 8,
    colors: ['#BF281B', '#FF3E1F', '#FF9B63', '#E7BC72', '#F8C71D', 'CDE334', '#9DC72D', '#4D9265']
  },
  {
    number_of_zones: 9,
    colors: [
      '#BF281B',
      '#FF3E1F',
      '#FF9B63',
      '#E7BC72',
      '#F8C71D',
      'CDE334',
      '#9DC72D',
      '#4D9265',
      '#006515'
    ]
  },
  {
    number_of_zones: 10,
    colors: [
      '#8F3A2E',
      '#BF281B',
      '#FF3E1F',
      '#FF9B63',
      '#E7BC72',
      '#F8C71D',
      'CDE334',
      '#9DC72D',
      '#4D9265',
      '#006515'
    ]
  }
];

export const calculateZoneAreaAndPercentages = (zones:any) => {
  // Calculate total area
  const totalArea = zones.reduce((sum:any, zone:any) => sum + area(zone.geometry), 0);

  // Calculate percentages
  return zones.map((zone:any) => {
    const zoneArea = area(zone.geometry);
    const percentage = (zoneArea / totalArea) * 100;
    return {
      ...zone,
      area: (zoneArea / 10000).toFixed(2),
      percentage: Math.round(percentage), // Round to nearest whole number,
      p50: zone.percentiles.p50.toFixed(2)
    };
  });
};

export const sortZonesByNDVIValue = (array:any) => {
  return array?.sort((a:any, b:any) => a.percentiles.p50 - b.percentiles.p50);
};

export const getAutoZoneDataForZone = (zones:any) => {
  return zones?.map((item:any) => {
    return {
      geometry: item.geometry,
      percentiles: item.ndvi_percentile,
      properties: item.properties || {}
    };
  });
};

export const getZoneColor = (numberOfZones:any, index:any) => {
  return zoneColors.find((item:any) => item.number_of_zones === numberOfZones)?.colors[index];
};

export const getZoneData = (fieldData:any, t:any) => {
  const zones = fieldData.productivity_zones;
  const numberOfZones = zones.length;
  return calculateZoneAreaAndPercentages(sortZonesByNDVIValue(getAutoZoneDataForZone(zones))).map(
    (zoneData:any, index:any) => {
      return {
        key: index,
        zones:
          numberOfZones > 5 ? `${t('Z')}-${index + 1}` : `${t('ZONE')}-${index + 1}`,
        hectares: zoneData.area,
        total: zoneData.percentage,
        ndvi: zoneData.p50,
        color: getZoneColor(numberOfZones, index),
        ...zoneData.percentiles
      };
    }
  );
};

// Get autozone GeoJSON features for a field
export const getFieldAutozoneGeoJsonFeatures = (field: any, t: any) => {
  const zones = field.productivity_zones;

  if (!zones?.length) {
    return [];
  }

  const zoneData = getZoneData(field, t);

  return zones.map((zone: any, index: number) => {
    return {
      type: 'Feature',
      properties: {
        zone: zoneData[index].zones,
        zoneColor: zoneData[index].color,
        fieldId: field.id,
        hectares: zoneData[index].hectares,
        percentage: zoneData[index].total,
        ndvi: zoneData[index].ndvi,
      },
      geometry: zone.geometry,
    };
  });
};
